import {Component, computed, DestroyRef, effect, inject, signal, viewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {finalize} from 'rxjs';
import {translate, TranslocoDirective, TranslocoPipe} from '@jsverse/transloco';
import {ButtonModule} from 'primeng/button';
import {Table, TableModule} from 'primeng/table';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {Menu, MenuModule} from 'primeng/menu';
import {CheckboxModule} from 'primeng/checkbox';
import {
  CreateUpdateProjectUserRequest,
  ProjectDetailResponse,
  ProjectRoleType,
  ProjectStepType,
  ProjectUserControllerService,
  ProjectUserDetailResponse,
  ProjectUserStatusType,
  SendInvitationDTO,
  SendInvitationsRequestParams,
} from '@frontend/shared-api';
import {AddEditUserDialogComponent} from '../add-edit-user-dialog/add-edit-user-dialog.component';
import {ProjectUserService} from '../project-user.service';
import {ToastService} from '@frontend/toast';
import {Card} from 'primeng/card';
import {ProjectUserDetailService} from '../../../../../shared/services/project-user-detail.service';
import {GlobalSearchComponent} from '../../../../../shared/components/search/global-search/global-search.component';

enum UserAction {
  TOGGLE_STATUS = 'toggleActiveStatus',
  DELETE = 'delete',
  REMOVE = 'remove',
}

@Component({
  selector: 'app-project-user-list',
  imports: [
    CommonModule,
    FormsModule,
    TranslocoDirective,
    TranslocoPipe,
    ButtonModule,
    TableModule,
    DialogModule,
    ToastModule,
    MenuModule,
    CheckboxModule,
    AddEditUserDialogComponent,
    Card,
    GlobalSearchComponent,
  ],
  providers: [MessageService],
  templateUrl: './project-user-list.component.html',
})
export class ProjectUserListComponent {
  readonly projectUserService = inject(ProjectUserService);
  private readonly projectUserControllerService = inject(ProjectUserControllerService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectUserDetailService = inject(ProjectUserDetailService);

  project = this.route.snapshot.data['project'] as ProjectDetailResponse;
  users = signal<ProjectUserDetailResponse[]>([]);
  loading = signal(false);
  showAddUserDialog = signal(false);
  showEditUserDialog = signal(false);
  selectedUser = signal<ProjectUserDetailResponse | null>(null);
  selectedUsers = signal<ProjectUserDetailResponse[]>([]);
  readonly rolesMap = computed(() => this.projectUserService.rolesMap());

  private dialogOpenedViaQueryParam = signal(false);
  readonly preselectedRoles = signal<ProjectRoleType[]>([]);
  readonly preselectedAccessiblePages = signal<ProjectStepType[]>([]);

  private readonly pollingActive = signal(false);
  private pollingTimeout: ReturnType<typeof setTimeout> | null = null;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;

  readonly table = viewChild.required<Table>('dt');
  readonly rowMenu = viewChild.required<Menu>('rowMenu');

  globalFilterFields = ['firstName', 'lastName', 'email', 'status', 'roles'];

  get selectedInvitableUsers(): ProjectUserDetailResponse[] {
    return this.selectedUsers().filter((user) => this.isInvitable(user));
  }

  get hasSelectedUsers(): boolean {
    return this.selectedUsers().length > 0;
  }

  constructor() {
    this.loadUsers();
    this.destroyRef.onDestroy(() => this.stopPolling());

    this.route.queryParams.subscribe((params) => {
      this.autoOpenAddUserDialog(params);
    });
    effect(() => {
      this.clearQueryParamWhenDialogClosed();
    });
  }

  loadUsers() {
    this.loading.set(true);
    this.projectUserControllerService
      .getProjectUsersWithDetails({projectId: this.project.id})
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (users) => this.users.set(users),
      });
  }

  openNewUserDialog() {
    this.showAddUserDialog.set(true);
  }

  handleAddUser(user: CreateUpdateProjectUserRequest) {
    this.loading.set(true);
    this.projectUserControllerService
      .assignProjectUser({
        projectId: this.project.id,
        createUpdateProjectUserRequest: user,
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.showAddUserDialog.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.toast.showSuccess('success.userAdded');
          this.loadUsers();
          this.projectUserDetailService.invalidateProjectUserDetail(this.project.id);
        },
      });
  }

  handleEditUser(request: CreateUpdateProjectUserRequest) {
    const user = this.selectedUser();
    if (!this.project.id || !user?.userId) {
      this.toast.showError('common.error', 'error.generic.detail');
      return;
    }

    this.loading.set(true);
    this.projectUserControllerService
      .updateProjectUser({
        projectId: this.project.id,
        userId: user.userId,
        createUpdateProjectUserRequest: request,
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.showEditUserDialog.set(false);
          this.selectedUser.set(null);
        }),
      )
      .subscribe({
        next: () => {
          this.toast.showSuccess('success.userUpdated');
          this.loadUsers();
          this.projectUserDetailService.invalidateProjectUserDetail(this.project.id);
        },
      });
  }

  editUser(user: ProjectUserDetailResponse): void {
    this.selectedUser.set(user);
    this.showEditUserDialog.set(true);
  }

  confirmToggleActivate(user: ProjectUserDetailResponse): void {
    const isActivating = user.status !== ProjectUserStatusType.ACTIVE;
    this.confirmUserAction(
      user,
      isActivating ? 'activate' : 'deactivate',
      UserAction.TOGGLE_STATUS,
    );
  }

  confirmDeleteUser(user: ProjectUserDetailResponse): void {
    this.confirmUserAction(user, 'delete', UserAction.DELETE);
  }

  confirmRemoveUser(user: ProjectUserDetailResponse): void {
    this.confirmUserAction(user, 'remove', UserAction.REMOVE);
  }

  private confirmUserAction(
    user: ProjectUserDetailResponse,
    actionType: 'activate' | 'deactivate' | 'delete' | 'remove',
    action: UserAction,
  ): void {
    const translationPrefix = `user.management.confirm${actionType.charAt(0).toUpperCase() + actionType.slice(1)}`;

    const headerKey = translationPrefix + '.header';
    const messageKey = translationPrefix + '.message';
    const userName = this.getUserName(user);

    let messageText: string;
    if (actionType === 'delete' || actionType === 'remove') {
      messageText = userName + ' ' + translate(messageKey, {name: userName});
    } else {
      messageText = translate(messageKey, {});
    }

    this.confirmationService.confirm({
      header: translate(headerKey),
      message: messageText,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.performUserAction(user, action);
      },
    });
  }

  private performUserAction(user: ProjectUserDetailResponse, action: UserAction): void {
    this.loading.set(true);

    const serviceCall = (() => {
      switch (action) {
        case UserAction.TOGGLE_STATUS:
          return this.projectUserControllerService.toggleUserActiveStatus({
            projectId: this.project.id,
            userId: user.userId,
          });
        case UserAction.DELETE:
          return this.projectUserControllerService.deleteUser({
            projectId: this.project.id,
            userId: user.userId,
          });
        case UserAction.REMOVE:
          return this.projectUserControllerService.removeUser({
            projectId: this.project.id,
            userId: user.userId,
          });
      }
    })();

    serviceCall.pipe(finalize(() => this.loading.set(false))).subscribe({
      next: () => {
        this.toast.showSuccess(
          `user.management.${action}.success.header`,
          `user.management.${action}.success.detail`,
        );
        this.loadUsers();
        this.projectUserDetailService.invalidateProjectUserDetail(this.project.id);
      },
    });
  }

  getUserName(user: ProjectUserDetailResponse): string {
    return `${user.firstName} ${user.lastName}`;
  }

  getUserRoles(user: ProjectUserDetailResponse): string {
    const roleMap = this.rolesMap();
    if (roleMap.size === 0) {
      return Array.from(user.roles).join(', ');
    }
    return Array.from(user.roles)
      .map((role) => roleMap.get(role) ?? role)
      .join(', ');
  }

  onRowMenuClick(event: MouseEvent, user: ProjectUserDetailResponse) {
    this.selectedUser.set(user);
    const rowMenu = this.rowMenu();
    rowMenu.model = this.getActionItems(user);
    rowMenu.toggle(event);
  }

  getActionItems(user: ProjectUserDetailResponse): MenuItem[] {
    return [
      {
        label: translate('common.edit'),
        icon: 'pi pi-pencil',
        command: () => this.editUser(user),
      },
      {
        label:
          user.status === ProjectUserStatusType.ACTIVE
            ? translate('common.deactivate')
            : translate('common.activate'),
        icon: 'pi pi-power-off',
        disabled:
          user.status !== ProjectUserStatusType.ACTIVE &&
          user.status !== ProjectUserStatusType.INACTIVE,
        command: () => this.confirmToggleActivate(user),
      },
      {
        label: `${translate('user.management.actions.removeFromProject')}`,
        icon: 'pi pi-user-minus',
        command: () => this.confirmRemoveUser(user),
      },
      {
        label: translate('common.delete'),
        icon: 'pi pi-trash',
        command: () => this.confirmDeleteUser(user),
      },
    ];
  }

  applyGlobalFilter(table: Table, event: Event) {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  isInvitable(user: ProjectUserDetailResponse): boolean {
    return (
      user.status !== ProjectUserStatusType.ACTIVE && user.status !== ProjectUserStatusType.INACTIVE
    );
  }

  getStatusLabel(status: ProjectUserStatusType): string {
    return this.projectUserService.getStatusLabel(status);
  }

  getStatusClass(status: ProjectUserStatusType): string {
    switch (status) {
      case ProjectUserStatusType.ACTIVE:
        return 'bg-green-100 text-green-800';
      case ProjectUserStatusType.INACTIVE:
        return 'bg-red-100 text-red-800';
      case ProjectUserStatusType.INVITED:
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  onSendInvitations(): void {
    if (this.loading() || !this.hasSelectedUsers) {
      return;
    }

    const selectedUsers = this.selectedInvitableUsers;
    const userNames = selectedUsers.map((user) => this.getUserName(user)).join(', ');

    const message = translate('user.management.sendInvitations.confirmMessage', {
      users: userNames,
    });

    this.confirmationService.confirm({
      header: translate('user.management.sendInvitations.confirmHeader'),
      message: message,
      icon: 'pi pi-envelope',
      accept: () => {
        this.sendInvitationsToSelectedUsers();
      },
    });
  }

  private sendInvitationsToSelectedUsers(): void {
    const selectedUsers = this.selectedInvitableUsers;
    const userIds: number[] = selectedUsers.map((u) => u.userId);

    const dto: SendInvitationDTO = {
      userIds: userIds,
    };

    this.loading.set(true);
    this.projectUserControllerService
      .sendInvitations({
        projectId: this.project.id,
        sendInvitationDTO: dto,
      } as SendInvitationsRequestParams)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.toast.showSuccess('success.invitationsSent');
          this.selectedUsers.set([]);
          this.loadUsers();
          this.startStatusPolling();
        },
      });
  }

  private startStatusPolling(): void {
    this.pollingActive.set(true);
    let pollCount = 0;

    this.pollingInterval = setInterval(() => {
      if (!this.pollingActive() || pollCount >= 10) {
        this.stopPolling();
        return;
      }

      pollCount++;
      this.pollUserStatus();
    }, 1000);
  }

  private pollUserStatus(): void {
    if (!this.pollingActive()) {
      return;
    }

    this.projectUserControllerService
      .getProjectUsersWithDetails({projectId: this.project.id})
      .subscribe({
        next: (users) => {
          this.users.set(users);
        },
      });
  }

  private stopPolling(): void {
    this.pollingActive.set(false);
    if (this.pollingTimeout) {
      clearTimeout(this.pollingTimeout);
      this.pollingTimeout = null;
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private clearQueryParamWhenDialogClosed() {
    if (this.dialogOpenedViaQueryParam() && !this.showAddUserDialog()) {
      this.dialogOpenedViaQueryParam.set(false);
      this.preselectedRoles.set([]);
      this.preselectedAccessiblePages.set([]);
      this.router.navigate([], {
        queryParams: {openAddDialog: null, preselectedRole: null, preselectedPages: null},
        queryParamsHandling: 'merge',
      });
    }
  }

  private autoOpenAddUserDialog(params: Params) {
    if (params['openAddDialog'] === 'true') {
      this.showAddUserDialog.set(true);
      this.dialogOpenedViaQueryParam.set(true);

      const roleParam = params['preselectedRole'];
      if (roleParam && Object.values(ProjectRoleType).includes(roleParam as ProjectRoleType)) {
        this.preselectedRoles.set([roleParam as ProjectRoleType]);
      }

      const pagesParam = params['preselectedPages'];
      if (pagesParam) {
        try {
          const pages = JSON.parse(pagesParam) as ProjectStepType[];
          this.preselectedAccessiblePages.set(pages);
        } catch (error) {
          console.error('Failed to parse preselectedPages query param:', error);
        }
      }
    }
  }
}
